import type { IWasteMovementEwcDefaultRule } from "@/customTypings/wasteMovement/WasteMovementDefaults";
import { stripDescriptionFromResolvedDisplay } from "./wasteMovementGeneralDefaultPreview";

const LOOKUP_DISPLAY_SEPARATOR = " — ";

export function splitLookupCodeAndDescription(display: string): { code: string; description: string | null } {
    const idx = display.indexOf(LOOKUP_DISPLAY_SEPARATOR);
    if (idx >= 0) {
        const description = display.slice(idx + LOOKUP_DISPLAY_SEPARATOR.length).trim();
        return { code: display.slice(0, idx).trim(), description: description || null };
    }
    return { code: display.trim(), description: null };
}

/** Target fields whose rule summary may show a responsive code + description pair. */
export function ewcRuleTargetShowsResponsiveDescription(fieldKey: string): boolean {
    return fieldKey === "RegDisposalRecoveryCodeId" || fieldKey === "RegPreTreatmentId";
}

/** EWC rule summary: show code only (no description) for these target fields. */
export const EWC_RULE_TARGET_CODE_ONLY_DISPLAY_FIELDS = new Set([
    "MunicipalFlagId",
    "DegradableFlagId",
    "RegPhysicalStateId",
    "RegSourceActivityId",
]);

export function formatEwcRuleTargetDisplay(fieldKey: string, display: string): string {
    if (EWC_RULE_TARGET_CODE_ONLY_DISPLAY_FIELDS.has(fieldKey)) {
        return stripDescriptionFromResolvedDisplay(display);
    }
    return display;
}

export type EwcRuleTargetDisplayEntry = {
    fieldKey: string;
    label: string;
    value: string;
};

export function getOrderedEwcRuleTargetEntries(
    rule: IWasteMovementEwcDefaultRule,
    fieldOrder: readonly string[],
    labelForField: (fieldKey: string) => string
): EwcRuleTargetDisplayEntry[] {
    return fieldOrder.flatMap((fieldKey) => {
        const raw = rule.targetDisplays[fieldKey];
        if (!raw) return [];
        return [
            {
                fieldKey,
                label: labelForField(fieldKey),
                value: formatEwcRuleTargetDisplay(fieldKey, raw),
            },
        ];
    });
}

export function getEwcRuleTriggerDisplays(rule: IWasteMovementEwcDefaultRule): string[] {
    if (rule.triggerEwcDisplays.length > 0) return rule.triggerEwcDisplays;
    return rule.triggerEwcIds;
}

export function isEwcRuleTargetCodeOnlySelectField(fieldKey: string): boolean {
    return EWC_RULE_TARGET_CODE_ONLY_DISPLAY_FIELDS.has(fieldKey);
}
